/**
 * Strict user-input validation helpers for public/user-facing fields.
 *
 * Owner requirements:
 * - Names: letters, hyphens, apostrophes, single spaces between words
 * - Addresses: alphanumeric + single spaces between words
 * - Email: letters, numbers, dots, one @, and a plus sign in the local part
 * - Phone: digits only, 7-15 chars
 * - Username: alphanumeric + underscore, 3-30 chars
 * - No double spaces, no trailing/leading spaces, no page breaks/newlines
 *
 * EXCLUDED from these rules: passwords, PINs, OTPs, tokens, wallet addresses,
 * transaction hashes, system IDs, file uploads.
 */

export interface ValidationResult {
  valid: boolean;
  error?: string;
  sanitized?: string;
}

/** Character length limits for user-facing fields */
export const FIELD_LIMITS = {
  username:        { min: 3,  max: 30  },
  firstName:       { min: 1,  max: 40  },
  middleName:      { min: 0,  max: 40  },
  lastName:        { min: 1,  max: 40  },
  fullName:        { min: 2,  max: 80  },
  email:           { min: 5,  max: 120 },
  phone:           { min: 7,  max: 15  },
  countryCode:     { max: 3   },
  city:            { max: 60  },
  province:        { max: 60  },
  postalCode:      { max: 12  },
  companyName:     { max: 80  },
  companyRegNumber:{ max: 64  },
  addressLine:     { max: 100 },
  suburb:          { max: 60  },
} as const;

/* ────── shared pre-checks ────── */

/** Returns an error string if value contains page breaks / control chars */
export function rejectPageBreaks(value: string): string | null {
  if (/[\n\r\t]/.test(value)) return 'Page breaks, tabs, and newlines are not allowed.';
  // eslint-disable-next-line no-control-regex
  if (/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/.test(value)) return 'Control characters are not allowed.';
  return null;
}

/** Trims leading/trailing spaces and collapses sequential spaces to a single space */
export function sanitizeNoDoubleSpaces(value: string): string {
  return value.trim().replace(/ {2,}/g, ' ');
}

/* ────── field validators ────── */

/**
 * Name fields: letters, hyphens, apostrophes, spaces between words.
 * No periods, no digits, no single letters. Full names must have first + last name.
 */
export function validateNameField(
  value: string,
  fieldName: string,
  opts?: { minLength?: number; maxLength?: number; optional?: boolean; requireFullName?: boolean }
): ValidationResult {
  const min = opts?.minLength ?? 1;
  const max = opts?.maxLength ?? 40;
  const trimmed = sanitizeNoDoubleSpaces(value);

  if (!trimmed) {
    if (opts?.optional) return { valid: true, sanitized: '' };
    return { valid: false, error: `${fieldName} is required.` };
  }
  const pb = rejectPageBreaks(trimmed);
  if (pb) return { valid: false, error: `${fieldName}: ${pb}` };
  if (trimmed.length < min) return { valid: false, error: `${fieldName} must be at least ${min} character(s).` };
  if (trimmed.length > max) return { valid: false, error: `${fieldName} must be at most ${max} characters.` };

  // Check for valid characters only (no periods)
  if (!/^[a-zA-Z\s\-']+$/.test(trimmed))
    return { valid: false, error: `${fieldName} may only contain letters, hyphens, apostrophes, and spaces. No periods or special characters.` };

  // Check for full name requirement (first + last)
  if (opts?.requireFullName) {
    const nameParts = trimmed.split(/\s+/);
    if (nameParts.length < 2) {
      return { valid: false, error: `${fieldName} must include both first and last name.` };
    }
    // Check each part is at least 2 characters (no single letters)
    for (const part of nameParts) {
      if (part.length < 2) {
        return { valid: false, error: `Each part of ${fieldName} must be at least 2 characters. No single letters.` };
      }
    }
  }

  return { valid: true, sanitized: trimmed };
}

/** Address/city/province: alphanumeric + single spaces between words */
export function validateAlphanumericField(
  value: string,
  fieldName: string,
  opts?: { minLength?: number; maxLength?: number; optional?: boolean }
): ValidationResult {
  const min = opts?.minLength ?? 1;
  const max = opts?.maxLength ?? 100;
  const trimmed = sanitizeNoDoubleSpaces(value);

  if (!trimmed) {
    if (opts?.optional) return { valid: true, sanitized: '' };
    return { valid: false, error: `${fieldName} is required.` };
  }
  const pb = rejectPageBreaks(trimmed);
  if (pb) return { valid: false, error: `${fieldName}: ${pb}` };
  if (trimmed.length < min) return { valid: false, error: `${fieldName} must be at least ${min} character(s).` };
  if (trimmed.length > max) return { valid: false, error: `${fieldName} must be at most ${max} characters.` };
  if (!/^[A-Za-z0-9]+( [A-Za-z0-9]+)*$/.test(trimmed))
    return { valid: false, error: `${fieldName} may only contain letters, numbers, and single spaces between words.` };
  return { valid: true, sanitized: trimmed };
}

/** Email: letters, numbers, dots, exactly one @, and + in the local part (Gmail plus addressing). */
export function validateStrictEmail(value: string): ValidationResult {
  const trimmed = value.trim();
  if (!trimmed) return { valid: false, error: 'Email is required.' };
  if (trimmed.length < FIELD_LIMITS.email.min) return { valid: false, error: 'Email is too short.' };
  if (trimmed.length > FIELD_LIMITS.email.max) return { valid: false, error: `Email must be at most ${FIELD_LIMITS.email.max} characters.` };
  // Plus is allowed only before @. The domain stays letters, numbers, and dots.
  if (!/^[A-Za-z0-9.+]+@[A-Za-z0-9.]+$/.test(trimmed))
    return { valid: false, error: 'Email may only contain letters, numbers, dots, a plus sign before the @, and one @ symbol. No spaces or other symbols.' };
  // Must have exactly one @
  if ((trimmed.match(/@/g) || []).length !== 1)
    return { valid: false, error: 'Email must contain exactly one @ symbol.' };
  // Must have at least one dot after @
  const domain = trimmed.split('@')[1];
  if (!domain || !domain.includes('.'))
    return { valid: false, error: 'Email must have a valid domain (e.g. example.com).' };
  return { valid: true, sanitized: trimmed.toLowerCase() };
}

/** Phone: digits only, 7-15 chars */
export function validatePhoneField(value: string): ValidationResult {
  const trimmed = value.trim();
  if (!trimmed) return { valid: false, error: 'Phone number is required.' };
  if (!/^[0-9]+$/.test(trimmed))
    return { valid: false, error: 'Phone number may only contain digits (0-9). No spaces, dashes, or country codes.' };
  if (trimmed.length < FIELD_LIMITS.phone.min || trimmed.length > FIELD_LIMITS.phone.max)
    return { valid: false, error: `Phone number must be ${FIELD_LIMITS.phone.min}-${FIELD_LIMITS.phone.max} digits.` };
  return { valid: true, sanitized: trimmed };
}

/** Postal code: alphanumeric only, max 12 */
export function validatePostalCode(value: string): ValidationResult {
  const trimmed = value.trim();
  if (!trimmed) return { valid: false, error: 'Postal code is required.' };
  if (trimmed.length > FIELD_LIMITS.postalCode.max)
    return { valid: false, error: `Postal code must be at most ${FIELD_LIMITS.postalCode.max} characters.` };
  if (!/^[A-Za-z0-9]+$/.test(trimmed))
    return { valid: false, error: 'Postal code may only contain letters and numbers.' };
  return { valid: true, sanitized: trimmed };
}

/** Username: alphanumeric + underscore, 3-30 chars */
export function validateUsernameField(value: string): ValidationResult {
  const trimmed = value.trim();
  if (!trimmed) return { valid: false, error: 'Username is required.' };
  if (trimmed.length < FIELD_LIMITS.username.min || trimmed.length > FIELD_LIMITS.username.max)
    return { valid: false, error: `Username must be ${FIELD_LIMITS.username.min}-${FIELD_LIMITS.username.max} characters.` };
  if (!/^[a-zA-Z0-9_]+$/.test(trimmed))
    return { valid: false, error: 'Username may only contain letters, numbers, and underscores.' };
  return { valid: true, sanitized: trimmed };
}
